import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Loader2, Mail, Phone, Send } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, formatApiError } from "@/lib/api";
import { ymd } from "./ShopSalesReport";
import { DataTools, RowDelete } from "./DataTools";
import { Chips, Empty, Loading, Stat, fmtWhen, useAdmin } from "./ui";

export const STATUS = [["open", "Open"], ["pending_customer", "Pending customer reply"], ["escalated", "Escalated"], ["closed", "Closed"]];
const LABEL = Object.fromEntries(STATUS);
const monthAgo = () => { const d = new Date(); d.setDate(d.getDate() - 29); return ymd(d); };
export const fmtWait = (m) => (m == null ? "—" : m < 60 ? `${m}m` : m < 1440 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${Math.floor(m / 1440)}d ${Math.floor((m % 1440) / 60)}h`);

const Thread = ({ conv, onSaved, onDeleted }) => {
  const [text, setText] = useState("");
  const [mode, setMode] = useState("reply");
  const [next, setNext] = useState("pending_customer");
  const [busy, setBusy] = useState(false);
  const run = async (fn, msg) => {
    setBusy(true);
    try { const { data } = await fn(); onSaved(data); setText(""); if (msg) toast.success(msg); } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  const send = (e) => {
    e.preventDefault();
    if (mode === "reply") run(() => api.post(`/admin/inbox/${conv.id}/reply`, { text, status: next }), `Reply emailed to ${conv.email}`);
    else run(() => api.post(`/admin/inbox/${conv.id}/customer`, { text }), "Customer reply added");
  };
  return (
    <>
      <DialogTitle className="ad-msg-title">{conv.subject}</DialogTitle>
      <p className="muted-sm">{conv.name} · {conv.email}{conv.phone && ` · ${conv.phone}`}</p>
      <div className="req-actions">
        <a className="btn light" href={`mailto:${conv.email}`} data-testid="inbox-email-link"><Mail size={14} /> Email</a>
        {conv.phone && <a className="btn light" href={`tel:${conv.phone}`} data-testid="inbox-call-link"><Phone size={14} /> Call</a>}
        <label className="mu-field ib-status">Status
          <select value={conv.status} disabled={busy} onChange={(e) => run(() => api.patch(`/admin/inbox/${conv.id}`, { status: e.target.value }), "Status updated")} data-testid="inbox-status-select">
            {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <RowDelete url={`/admin/inbox/${conv.id}`} name={`the conversation with ${conv.name}`} onDone={onDeleted} testId="inbox-delete-button" />
      </div>
      <div className="ib-thread" data-testid="inbox-thread">
        {conv.thread.map((m, i) => (
          <div key={`${m.at}-${m.from}-${i}`} className={`ib-msg ${m.from}`} data-testid={`inbox-thread-msg-${i}`}>
            <small>{m.from === "spa" ? `Wax Moi${m.by ? ` (${m.by})` : ""}` : conv.name}{m.pasted && " · pasted from email"}{m.inbound && " · replied by email"} · {fmtWhen(m.at)}</small>
            <p>{m.text}</p>
          </div>
        ))}
      </div>
      <form className="ib-reply" onSubmit={send} data-testid="inbox-reply-form">
        <Chips options={[["reply", "Reply to customer"], ["customer", "Add customer reply"]]} value={mode} onChange={setMode} testPrefix="inbox-mode" />
        <textarea required rows={4} maxLength={5000} value={text} onChange={(e) => setText(e.target.value)}
          placeholder={mode === "reply" ? `Write your reply to ${conv.name.split(" ")[0]}…` : "Paste the customer's email reply here"} data-testid="inbox-reply-input" />
        <div className="req-actions">
          {mode === "reply" && (
            <label className="mu-field ib-status">Then set status to
              <select value={next} onChange={(e) => setNext(e.target.value)} data-testid="inbox-next-status-select">
                {STATUS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </label>
          )}
          <button className="btn gold" type="submit" disabled={busy || !text.trim()} data-testid="inbox-send-button">
            {busy ? <Loader2 size={15} className="spin" /> : mode === "reply" ? <><Send size={14} /> Send reply</> : "Add to conversation"}
          </button>
        </div>
      </form>
    </>
  );
};

const Stats = ({ data }) => (
  <div className="ad-stats" data-testid="inbox-summary">
    <Stat label="Messages" value={data.total} testId="inbox-stat-total" />
    <Stat label="Avg first reply" value={fmtWait(data.avg_reply_minutes)} hint={`${data.replied} replied`} testId="inbox-stat-avg-reply" />
    {STATUS.map(([k, l]) => <Stat key={k} label={l} value={data.counts[k]} testId={`inbox-stat-${k}`} />)}
  </div>
);

export const Inbox = () => {
  const [from, setFrom] = useState(monthAgo);
  const [to, setTo] = useState(() => ymd(new Date()));
  const [filter, setFilter] = useState("all");
  const [openId, setOpenId] = useState(null);
  const qc = useQueryClient();
  const key = `inbox?start=${from}&end=${to}`;
  const { data } = useAdmin(key, `/admin/inbox?start=${from}&end=${to}`);
  const conv = data?.items.find((c) => c.id === openId);
  const shown = (data?.items || []).filter((c) => filter === "all" || c.status === filter);
  const saved = () => qc.invalidateQueries({ queryKey: ["admin", key] });
  return (
    <div data-testid="inbox">
      <div className="sales-filters">
        <label className="mu-field">From<input type="date" value={from} max={to} onChange={(e) => e.target.value && setFrom(e.target.value)} data-testid="inbox-from-input" /></label>
        <label className="mu-field">To<input type="date" value={to} min={from} onChange={(e) => e.target.value && setTo(e.target.value)} data-testid="inbox-to-input" /></label>
      </div>
      {!data ? <Loading /> : (
        <>
          <Stats data={data} />
          <DataTools section="inbox" label="conversations" exportUrl={`/admin/inbox/export?start=${from}&end=${to}`} filename={`waxmoi-messages_${from}_${to}.xlsx`} onPurged={() => qc.invalidateQueries({ queryKey: ["admin"] })} />
          <Chips options={[["all", `All (${data.total})`], ...STATUS.map(([k, l]) => [k, `${l} (${data.counts[k]})`])]} value={filter} onChange={setFilter} testPrefix="inbox-filter" />
          {shown.length === 0 ? <Empty>No customer messages in this date range.</Empty> : (
            <div className="ad-table">
              {shown.map((c) => (
                <button className="ad-trow ad-trow-btn" key={c.id} onClick={() => setOpenId(c.id)} data-testid={`inbox-row-${c.id}`}>
                  <div><strong>{c.name}</strong><small>{c.subject}</small><small className="ib-preview">{c.thread[c.thread.length - 1].text}</small></div>
                  <div><small>Received {fmtWhen(c.created_at)}</small><small>{c.thread.length} message{c.thread.length > 1 ? "s" : ""}</small></div>
                  <div className="ad-tright"><span className={`pill ib-${c.status}`} data-testid="inbox-row-status">{LABEL[c.status]}</span></div>
                </button>
              ))}
            </div>
          )}
        </>
      )}
      <Dialog open={!!conv} onOpenChange={(o) => !o && setOpenId(null)}>
        <DialogContent className="ad-msg-dialog" data-testid="inbox-dialog">
          {conv && <Thread key={conv.id} conv={conv} onSaved={saved} onDeleted={() => { setOpenId(null); saved(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
};
